import { useMemo } from 'react';
import { DataTable, type DataTableColumnDef } from 'components/data-table';
import { Link } from 'components/link';
import { formatTitleRiskValue } from 'pages/crm-tracts-subtracts/utils';
import {
  DECIMAL_8,
  EMPTY,
  linkCellRenderer,
  NESTED_TABLE_MULTISELECT_CONFIG,
  wellStatusColumn,
} from './utils';
import { useChildHighlightSelection, useInertChildSelection } from './hooks';
import { TotalSlotInterestCell } from './total-slot-interest-cell';
import type { AssetRow, AssetSubtractRow, SlotAllocationRow } from './types';
import styles from './asset-expanded-row.module.scss';

type SlotTableRow = SlotAllocationRow & Record<string, unknown>;
type SubtractTableRow = AssetSubtractRow & Record<string, unknown>;

/**
 * Slot subtable columns for an expanded Asset row — the slot allocations that
 * touch the tract, per mock UI v2's `CHILD_COLUMNS_FOR_ASSETS` (slot-side
 * columns). Headers/order match the mock exactly. Slot ID links via the BE
 * `slotIdHref`; Total Slot NRI/WI render through `TotalSlotInterestCell` (the
 * allocation rows carry their subtract rollup + tract/slot identity, so the
 * calculation + NRI-matrix popups work the same as the Slot Allocation view).
 */
const SLOT_COLUMNS: DataTableColumnDef<SlotTableRow>[] = [
  {
    accessorKey: 'slotId',
    header: 'Slot ID',
    size: 140,
    cell: (info) =>
      linkCellRenderer(info.getValue<string>(), info.row.original.slotIdHref),
  },
  {
    accessorKey: 'wellName',
    header: 'Well Name',
    size: 180,
    meta: { type: 'text' },
  },
  { accessorKey: 'unit', header: 'Unit', size: 140, meta: { type: 'text' } },
  {
    accessorKey: 'formation',
    header: 'Formation',
    size: 140,
    meta: { type: 'text' },
  },
  {
    accessorKey: 'totalSlotNri',
    header: 'Total Slot NRI',
    // 400 (legacy parity): fits value + source pill + icons (was 200).
    size: 400,
    meta: { type: 'decimal', wrap: true },
    cell: (info) => (
      <TotalSlotInterestCell
        row={info.row.original}
        value={info.getValue<number | null>()}
        calculationType="NRI"
      />
    ),
  },
  {
    accessorKey: 'totalSlotWi',
    header: 'Total Slot WI',
    size: 400,
    meta: { type: 'decimal', wrap: true },
    cell: (info) => (
      <TotalSlotInterestCell
        row={info.row.original}
        value={info.getValue<number | null>()}
        calculationType="WI"
      />
    ),
  },
  wellStatusColumn<SlotTableRow>(),
];

/**
 * CRM-subtract subtable columns for an expanded Asset row — the tract's distinct
 * subtracts (flattened + deduped from the allocation children's nested subtracts).
 * Ported from the legacy CRM Tracts subtract table (`components/crm-tracts-table`):
 * Name / Net Acres / Gross Acres / Depth Range / Title Quality / Title Risk Factor.
 * Name links to the CRM Tract Wizard edit route (polymorphic on the subtract
 * salesforce id). Depth Range reads the BE's preformatted `depthRange` string (the
 * `assets` grain emits no numeric start/end depth), so it renders as plain text.
 */
const CRM_SUBTRACT_COLUMNS: DataTableColumnDef<SubtractTableRow>[] = [
  {
    accessorKey: 'name',
    header: 'Name',
    size: 200,
    cell: (info) => {
      const value = info.getValue<string>();
      const { salesforceId } = info.row.original;
      if (!value || !salesforceId) {
        return value || EMPTY;
      }
      return (
        <Link
          to={`/crm/tracts_and_subtracts/${encodeURIComponent(
            salesforceId
          )}/edit`}
          openInNewTab
          onClick={(e) => e.stopPropagation()}
        >
          {value}
        </Link>
      );
    },
  },
  { accessorKey: 'netAcres', header: 'Net Acres', size: 130, meta: DECIMAL_8 },
  {
    accessorKey: 'grossAcres',
    header: 'Gross Acres',
    size: 130,
    meta: DECIMAL_8,
  },
  {
    accessorKey: 'depthRange',
    header: 'Depth Range',
    size: 130,
    meta: { type: 'text' },
  },
  {
    accessorKey: 'titleQuality',
    header: 'Title Quality',
    size: 130,
    cell: (info) => formatTitleRiskValue(info.getValue<string | null>(), EMPTY),
  },
  {
    accessorKey: 'titleRiskFactor',
    header: 'Title Risk Factor',
    size: 150,
    cell: (info) => formatTitleRiskValue(info.getValue<string | null>(), EMPTY),
  },
];

interface AssetExpandedRowProps {
  row: AssetRow;
}

/**
 * Expanded content for an Asset row: two stacked subtables — the slot subtable
 * (the slot allocations touching the tract, mock UI v2 "Assets view shows
 * slot-allocation children") and the CRM-subtract subtable (the tract's distinct
 * subtracts, retained from the legacy CRM Tracts expansion). Data comes from the
 * adapted `row.slotAllocations` and `row.subtracts` (both derived from the BE
 * asset `children`). Renders nothing when the tract has neither.
 *
 * @param {AssetExpandedRowProps} props - The parent asset row supplying both subtables' data.
 * @return {JSX.Element | null} The stacked subtables, or null when both are empty.
 */
export function AssetExpandedRow({ row }: AssetExpandedRowProps) {
  const slots = useMemo(
    () => (row.slotAllocations ?? []) as SlotTableRow[],
    [row.slotAllocations]
  );
  const subtracts = useMemo(
    () => (row.assetSubtracts ?? []) as SubtractTableRow[],
    [row.assetSubtracts]
  );

  const parentRow = row as unknown as Record<string, unknown>;

  // Slot-Allocations checkboxes drive the map highlight: each checked slot child
  // contributes its well (+ unit) to the pinned tract's scope; unchecking excludes
  // it. Toggling auto-pins this asset row (see `useChildHighlightSelection`).
  const slotChildIds = useMemo(
    () => slots.map((slot) => String(slot.id)),
    [slots]
  );
  const slotSelection = useChildHighlightSelection(parentRow, slotChildIds);

  // CRM-Subtracts checkboxes are map-inert (subtract names match no feature), but
  // still follow this row's pin state for visual parity with the Slot-Allocations
  // table — checked while pinned, cleared on unpin.
  const subtractChildIds = useMemo(
    () => subtracts.map((subtract) => String(subtract.salesforceId)),
    [subtracts]
  );
  const subtractSelection = useInertChildSelection(
    String(parentRow.id ?? ''),
    subtractChildIds
  );

  if (slots.length === 0 && subtracts.length === 0) {
    return null;
  }

  return (
    <div className={styles.container}>
      {slots.length > 0 && (
        <div className={styles.section}>
          <span className={styles.sectionTitle}>Property Allocations</span>
          <div className={styles.nestedTable}>
            <DataTable<SlotTableRow>
              data={slots}
              columns={SLOT_COLUMNS}
              getRowId={(slot) => String(slot.id)}
              config={NESTED_TABLE_MULTISELECT_CONFIG}
              controlledState={{ selection: slotSelection.selection }}
              onSelectionChange={slotSelection.onSelectionChange}
            />
          </div>
        </div>
      )}
      {subtracts.length > 0 && (
        <div className={styles.section}>
          <span className={styles.sectionTitle}>CRM Subtracts</span>
          <div className={styles.nestedTable}>
            <DataTable<SubtractTableRow>
              data={subtracts}
              columns={CRM_SUBTRACT_COLUMNS}
              getRowId={(subtract) => String(subtract.salesforceId)}
              config={NESTED_TABLE_MULTISELECT_CONFIG}
              controlledState={{ selection: subtractSelection.selection }}
              onSelectionChange={subtractSelection.onSelectionChange}
            />
          </div>
        </div>
      )}
    </div>
  );
}
